import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCarSide, FaVanShuttle, FaBusSimple, FaBus, FaLandmark } from "react-icons/fa6";
import {
  FiMapPin,
  FiCalendar,
  FiClock,
  FiUser,
  FiCreditCard,
  FiMap,
} from "react-icons/fi";
import { IoCalendarOutline, IoClose } from "react-icons/io5";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import TourSelection from "../TourSelection/TourSelection";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import TourForm from "../TourForm/TourForm";

const AnimatedCar = ({ vehicleType, activeStep }) => {
  // If no vehicle type is selected, don't show
  if (!vehicleType) return null;

  const getCarImage = () => {
    switch (vehicleType) {
      case "car":
        return require("../../assets/images/emerald_green.webp");
      case "minivan":
        return require("../../assets/images/mercedes-vito_tourer_1.png");
      case "van":
        return require("../../assets/images/V-Class-Marco-Polo-1.png");
      case "minibus":
        return require("../../assets/images/fleet-Mercedes-Benz-Sprinter-Passenger-Van.png");
      default:
        return require("../../assets/images/mercedes-vito_tourer_1.png");
    }
  };

  return (
    <motion.div
      initial={{ x: "100%", opacity: 0 }}
      animate={{ x: "0%", opacity: 1 }}
      transition={{ duration: 1.2, type: "spring", bounce: 0.3 }}
      className="transform max-w-md z-10 lg:block hidden"
      style={{
        pointerEvents: "none",
      }}
    >
      <motion.img
        key={vehicleType}
        src={getCarImage()}
        alt="Luxury transfer car"
        className="w-full h-auto object-contain drop-shadow-2xl hover:scale-105 transition-transform lg:block hidden"
        initial={{ scale: 0.9, opacity: 0.5 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        style={{
          maxWidth: "100%",
          maxHeight: "80vh",
        }}
      />
    </motion.div>
  );
};

const TransferForm = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicleType, setVehicleType] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [formData, setFormData] = useState({
    pickUpDate: new Date(),
    dropOffDate: new Date(),
    returnDate: new Date(),
    pickupLocation: "",
    dropoffLocation: "",
    otherPickupLocation: "",
    otherDropoffLocation: "",
    returnLocation: "",
    otherReturnLocation: "",
    pickupHours: "12",
    pickupMinutes: "00",
    dropoffHours: "12",
    dropoffMinutes: "00",
    returnHours: "12",
    returnMinutes: "00",
    name: "",
    surname: "",
    email: "",
    phone: "",
    persons: "",
    luggage: "",
    serviceType: "one-way",
    carType: "",
    additionalInfo: "",
    paymentMethod: "credit-card",
    isReturn: false,
    airportTicketId: "",
    helicopterTransfer: false,
    atDisposal: false,
    disposalHours: "3",
    // Tour booking related fields
    includeTour: false,
    tourId: "",
    tourDate: new Date(),
    tourTime: { hours: "10", minutes: "00" },
    travelGuide: {
      available: false,
      time: '',
    },
    tourPersons: 2,
    tourPrice: "0",
    tourName: "",
  });

  const [timeWarnings, setTimeWarnings] = useState({
    pickup: false,
    dropoff: false,
  });

  const [errors, setErrors] = useState({});
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [returnPriceEstimate, setReturnPriceEstimate] = useState(null);
  const [totalPriceEstimate, setTotalPriceEstimate] = useState(null);
  const [season, setSeason] = useState("");
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [priceToBeDecided, setPriceToBeDecided] = useState(false);

  const [location, setLocation] = useState(null);

  useEffect(() => {
    // Wait for DOM to be ready
    if (activeStep === 2) {
      const phoneInput = document.getElementsByClassName("PhoneInputInput")[0];
      if (phoneInput) {
        phoneInput.className =
          "PhoneInputInput rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm";
      }
    }
  }, [activeStep]);

  // Get price estimate when relevant fields change
  useEffect(() => {
    const getPriceEstimate = async () => {
      // Only get price if both locations and vehicle type are set
      if (
        formData.pickupLocation &&
        formData.dropoffLocation &&
        formData.carType
      ) {
        try {
          setLoadingPrice(true);
          const pickup =
            formData.pickupLocation === "other"
              ? "other"
              : formData.pickupLocation;
          const dropoff =
            formData.dropoffLocation === "other"
              ? "other"
              : formData.dropoffLocation;

          // Don't fetch price for custom locations
          if (pickup === "other" || dropoff === "other") {
            setPriceEstimate(null);
            setReturnPriceEstimate(null);
            setTotalPriceEstimate(null);
            setSeason("");
            setPriceToBeDecided(true);
            setLoadingPrice(false);
            return;
          }

          const date = formData.pickUpDate;
          const serviceType = formData.isReturn ? "round-trip" : "one-way";
          const carType = formData.carType || "car";

          // Add return location if this is a round trip
          let returnLocation = null;
          if (formData.isReturn && formData.returnLocation) {
            returnLocation =
              formData.returnLocation === "other"
                ? "other"
                : formData.returnLocation;
          }

          const response = await axios.get(
            `http://localhost:5000/api/contact/transfer-price`,
            {
              params: {
                pickup,
                dropoff,
                returnLocation,
                date,
                serviceType,
                carType,
              },
            }
          );


          if (response.data) {
            // Set season regardless of whether price is decided
            setSeason(response.data.season || "medium");

            // FIXED: Check if price exists and is > 0, regardless of priceToBeDecided flag
            if (response.data.price && response.data.price > 0) {
              // We have a valid price, so use it regardless of priceToBeDecided flag
              setPriceToBeDecided(false);

              // Handle one-way vs round-trip pricing
              if (formData.isReturn && returnLocation) {
                // For round trip we need to calculate separately
                // The server returns total price, now we need to determine one-way part

                // First get one-way price (without the return param)
                const oneWayResponse = await axios.get(
                  `http://localhost:5000/api/contact/transfer-price`,
                  {
                    params: {
                      pickup,
                      dropoff,
                      date,
                      serviceType: "one-way",
                      carType,
                    },
                  }
                );

                if (oneWayResponse.data && oneWayResponse.data.price > 0) {
                  const oneWayPrice = oneWayResponse.data.price;
                  setPriceEstimate(oneWayPrice);
                  setReturnPriceEstimate(response.data.price - oneWayPrice);
                  setTotalPriceEstimate(response.data.price);
                } else {
                  // If one-way lookup fails, just use total price
                  setPriceEstimate(response.data.price);
                  setReturnPriceEstimate(0);
                  setTotalPriceEstimate(response.data.price);
                }
              } else {
                // Simple one-way price
                setPriceEstimate(response.data.price);
                setReturnPriceEstimate(0);
                setTotalPriceEstimate(response.data.price);
              }
            } else {
              // No valid price
              setPriceToBeDecided(true);
              setPriceEstimate(0);
              setReturnPriceEstimate(0);
              setTotalPriceEstimate(0);
            }
          } else {
            setPriceEstimate(null);
            setReturnPriceEstimate(null);
            setTotalPriceEstimate(null);
            setSeason("");
            setPriceToBeDecided(false);
          }
        } catch (error) {
          console.error("Error fetching price estimate:", error);
          setPriceEstimate(null);
          setReturnPriceEstimate(null);
          setTotalPriceEstimate(null);
          setSeason("");
          setPriceToBeDecided(false);
        } finally {
          setLoadingPrice(false);
        }
      } else {
        // Reset price if data is incomplete
        setPriceEstimate(null);
        setReturnPriceEstimate(null);
        setTotalPriceEstimate(null);
        setSeason("");
        setPriceToBeDecided(false);
      }
    };

    getPriceEstimate();
  }, [
    formData.pickupLocation,
    formData.dropoffLocation,
    formData.returnLocation,
    formData.pickUpDate,
    formData.carType,
    formData.isReturn,
  ]);

  useEffect(() => {
    // Get user's location if on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // Check if we have cached location data first
      const cachedLocationData = localStorage.getItem("userLocationData");
      let cachedLocation = null;

      if (cachedLocationData) {
        try {
          const parsedData = JSON.parse(cachedLocationData);
          // Check if the cached data is less than 1 hour old
          const cacheTime = new Date(parsedData.timestamp);
          const currentTime = new Date();
          const cacheAgeHours = (currentTime - cacheTime) / (1000 * 60 * 60);

          if (cacheAgeHours < 1) {
            // Use the cached location if it's recent
            cachedLocation = parsedData;

            // Set the location data from cache
            setLocation({
              lat: parsedData.lat,
              lng: parsedData.lng,
            });

            setFormData((prev) => ({
              ...prev,
              pickupLocation: "other",
              otherPickupLocation: parsedData.address,
            }));

            // No need to show the loading indicator since we're using cached data
            return;
          }
        } catch (e) {
          console.error("Error parsing cached location data:", e);
          // Continue with getting new location if cache parsing fails
        }
      }

      setLoadingLocation(true);

      const timeoutId = setTimeout(() => {
        if (loadingLocation) {
          setLoadingLocation(false);
          setFormData((prev) => ({
            ...prev,
            pickupLocation: "other",
            otherPickupLocation: "My current location",
          }));
        }
      }, 50000);

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            setLocation(coords);

            // Set coordinates immediately while we try to get the address
            setFormData((prev) => ({
              ...prev,
              pickupLocation: "other",
              otherPickupLocation: `${position.coords.latitude.toFixed(
                5
              )}, ${position.coords.longitude.toFixed(5)}`,
            }));

            // Attempt to get location name using reverse geocoding
            fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${position.coords.latitude},${position.coords.longitude}&key=AIzaSyDMynCGNny6phPgprUn1v44DVlRxmSxI1o`
            )
              .then((response) => {
                if (!response.ok) {
                  throw new Error("Network response was not ok");
                }
                return response.json();
              })
              .then((data) => {
                if (data.status === "OK" && data.results.length > 0) {
                  const address = data.results[0].formatted_address;

                  // Cache the location data with timestamp
                  const locationData = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    address: address,
                    timestamp: new Date().toISOString(),
                  };

                  localStorage.setItem(
                    "userLocationData",
                    JSON.stringify(locationData)
                  );

                  setFormData((prev) => ({
                    ...prev,
                    pickupLocation: "other",
                    otherPickupLocation: address,
                  }));
                }
                // Always turn off loading state
                setLoadingLocation(false);
              })
              .catch((error) => {
                console.error("Error fetching address:", error);
                // Keep the coordinates as the location if geocoding fails
                setLoadingLocation(false);
              });
          },
          (error) => {
            clearTimeout(timeoutId);
            console.error("Geolocation error:", error);
            setLoadingLocation(false);
          },
          { enableHighAccuracy: true, timeout: 50000, maximumAge: 0 }
        );
      } else {
        // Browser doesn't support geolocation
        clearTimeout(timeoutId);
        setLoadingLocation(false);
      }

      // Cleanup function
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const locationOptions = [
    { value: "AthensAirport", label: "Athens Airport", icon: "✈️" },
    { value: "AthensCityHotels", label: "Athens City/Hotels", icon: "🏙️" },
    { value: "PiraeusPort", label: "Piraeus Port", icon: "🚢" },
    { value: "PiraeusCity", label: "Piraeus City", icon: "🏙️" },
    { value: "LavrioPort", label: "Lavrio Port", icon: "🚢" },
    { value: "LavrioCity", label: "Lavrio City", icon: "🏙️" },
    { value: "other", label: "Other Location", icon: "📍" },
  ];

  const carTypeOptions = [
    {
      value: "car",
      label: "Car up to 2 pax",
      icon: FaCarSide,
      description: "Luxury sedan for up to 2 passengers",
      minPassengers: 1,
      maxPassengers: 2,
      maxLuggage: 2,
    },
    {
      value: "minivan",
      label: "Mini Van up to 7 pax",
      icon: FaVanShuttle,
      description: "Spacious minivan for up to 7 passengers",
      minPassengers: 3,
      maxPassengers: 7,
      maxLuggage: 7,
    },
    {
      value: "van",
      label: "Van up to 12 pax",
      icon: FaBusSimple,
      description: "Large van for up to 12 passengers",
      minPassengers: 8,
      maxPassengers: 12,
      maxLuggage: 12,
    },
    {
      value: "minibus",
      label: "Mini Bus up to 20 pax",
      icon: FaBus,
      description: "Mini bus for up to 20 passengers",
      minPassengers: 13,
      maxPassengers: 20,
      maxLuggage: 20,
    },
  ];

  const disposalHoursOptions = [
    { value: "3", label: "3 Hours" },
    { value: "6", label: "6 Hours" },
    { value: "12", label: "12 Hours" },
    { value: "24", label: "24 Hours" },
    { value: "custom", label: "Custom" },
  ];

  const validateCapacity = () => {
    const currentVehicle = carTypeOptions.find(
      (opt) => opt.value === vehicleType
    );
    const newErrors = {};

    if (!currentVehicle) return newErrors;

    const persons = parseInt(formData.persons);
    const luggage = parseInt(formData.luggage);

    if (persons > currentVehicle.maxPassengers) {
      newErrors.persons = `This vehicle can only accommodate up to ${currentVehicle.maxPassengers} passengers`;
      const recommendedVehicle = carTypeOptions.find(
        (v) => v.maxPassengers >= persons
      );
      if (recommendedVehicle && recommendedVehicle.value !== vehicleType) {
        newErrors.personsRecommendation = `Please consider booking a ${recommendedVehicle.label} instead`;
      }
    } else if (persons < currentVehicle.minPassengers) {
      newErrors.persons = `This vehicle requires a minimum of ${currentVehicle.minPassengers} passengers`;
      const recommendedVehicle = carTypeOptions.find(
        (v) => v.minPassengers <= persons
      );
      if (recommendedVehicle && recommendedVehicle.value !== vehicleType) {
        newErrors.personsRecommendation = `Please consider booking a ${recommendedVehicle.label} instead`;
      }
    }

    if (luggage > currentVehicle.maxLuggage) {
      newErrors.luggage = `This vehicle can only accommodate up to ${currentVehicle.maxLuggage} luggage items`;
      const recommendedVehicle = carTypeOptions.find(
        (v) => v.maxLuggage >= luggage
      );
      if (recommendedVehicle && recommendedVehicle.value !== vehicleType) {
        newErrors.luggageRecommendation = `Please consider booking a ${recommendedVehicle.label} instead`;
      }
    }

    return newErrors;
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Handle return toggle
    if (name === "isReturn" && type === "checkbox") {
      if (checked) {
        // Enable return mode, but keep existing dropoff values
        setFormData((prev) => ({
          ...prev,
          isReturn: true,
        }));
      } else {
        // If unchecking return, sync dropoff with pickup
        setFormData((prev) => ({
          ...prev,
          isReturn: false,
          dropOffDate: prev.pickUpDate,
          dropoffHours: prev.pickupHours,
          dropoffMinutes: prev.pickupMinutes,
          dropoffLocation: prev.pickupLocation,
          otherDropoffLocation: prev.otherPickupLocation,
        }));
      }
    }

    // Clear error when field is changed
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle date changes
  const handleDateChange = (date, type) => {
    const isPickup = type === "pickUpDate";
    const stateKey = isPickup ? "pickup" : "dropoff";

    // Check if both date and time are set
    const hasHours = formData[isPickup ? "pickupHours" : "dropoffHours"];
    const hasMinutes = formData[isPickup ? "pickupMinutes" : "dropoffMinutes"];
    const hasTime = hasHours && hasMinutes;

    // Show warning if time is not selected
    setTimeWarnings((prev) => ({
      ...prev,
      [stateKey]: !hasTime,
    }));

    setFormData((prev) => ({
      ...prev,
      [type]: date,
      // If changing pickup date and it's after dropoff, update dropoff date
      ...(isPickup && date > prev.dropOffDate ? { dropOffDate: date } : {}),
    }));
  };
  // Select vehicle type
  const handleVehicleSelect = (type) => {
    setVehicleType(type);
    setFormData((prev) => ({ ...prev, carType: type }));
    // Stays on same step until user clicks continue
  };
  // Handle tour selection
  const handleTourSelect = (tourData) => {
    if (tourData) {
      setFormData({
        ...formData,
        includeTour: true,
        tourId: tourData.tourId,
        tourDate: tourData.tourDate,
        tourTime: tourData.tourTime,
        tourPersons: tourData.tourPersons,
        travelGuide: {
          available: !!tourData.travelGuide.available,
          time: tourData.travelGuide.available ? tourData.travelGuide.time : "",
        },
        tourPrice: tourData.tourPrice,
        tourName: tourData.tourName,
      });
    } else {
      setFormData({
        ...formData,
        includeTour: false,
        tourId: "",
        tourDate: new Date(),
        tourTime: { hours: "10", minutes: "00" },
        tourPersons: formData.persons || 2,
        travelGuide: {
          available: false,
          time: "",
        },
        tourPrice: 0,
        tourName: "",
      });
    }
  };

  // Add history handling for mobile back gesture
  useEffect(() => {
    // Function to handle browser's back button or gesture
    const handleBackNavigation = (event) => {
      // Only intercept back navigation if we're past the first step
      if (activeStep > 1) {
        // Prevent the default back behavior
        event.preventDefault();

        // Go back one step in the form instead
        prevStep();

        // Push a new entry to maintain history stack
        window.history.pushState(
          null,
          document.title,
          window.location.pathname
        );

        return;
      }
      // If on first step, allow normal back functionality
    };

    // Add a history entry when component mounts
    window.history.pushState(null, document.title, window.location.pathname);

    // Listen for popstate events (back navigation)
    window.addEventListener("popstate", handleBackNavigation);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("popstate", handleBackNavigation);
    };
  }, [activeStep]); // Re-establish listener when active step changes

  // Navigate between steps
  const nextStep = () => {
    // Validate fields before proceeding
    const newErrors = {};

    if (activeStep === 1) {
      if (!vehicleType) {
        newErrors.vehicleType = "Please select a vehicle type";
      }
      if (!formData.pickupLocation) {
        newErrors.pickupLocation = "Please select a pickup location";
      }
      if (
        formData.pickupLocation === "other" &&
        !formData.otherPickupLocation
      ) {
        newErrors.otherPickupLocation = "Please enter the pickup location";
      }
      if (!formData.dropoffLocation) {
        newErrors.dropoffLocation = "Please select a drop-off location";
      }
      if (
        formData.dropoffLocation === "other" &&
        !formData.otherDropoffLocation
      ) {
        newErrors.otherDropoffLocation = "Please enter the drop-off location";
      }
      if (formData.isReturn) {
        if (!formData.returnLocation) {
          newErrors.returnLocation = "Please select a return location";
        }
      }
      if (!formData.pickupHours) {
        newErrors.pickupHours = "Please select a pickup hour";
      }
      if (formData.pickUpDate > formData.dropOffDate) {
        newErrors.pickUpDate = "Pickup date cannot be after drop-off date";
      }
      if (formData.dropOffDate < formData.pickUpDate) {
        newErrors.dropOffDate = "Drop-off date cannot be before pickup date";
      }
    } else if (activeStep === 2) {
      if (!formData.name) newErrors.name = "Please enter your first name";
      if (!formData.surname) newErrors.surname = "Please enter your last name";
      if (!formData.email) {
        newErrors.email = "Please enter your email";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!formData.phone) {
        newErrors.phone = "Please enter your phone number";
      }
      //   if phone does not contain area code, e.g. +30, +1, etc.
      if (formData.phone && !/^\+\d{1,3}\d{9,}$/.test(formData.phone)) {
        newErrors.phone = "Please enter a valid phone number with area code";
      }
      if (!formData.persons) {
        newErrors.persons = "Please enter number of passengers";
      }

      // Add capacity validation
      const capacityErrors = validateCapacity();
      Object.assign(newErrors, capacityErrors);
    }

    const capacityErrors = validateCapacity();
    Object.assign(newErrors, capacityErrors);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep((prev) => prev + 1);
      setIsAnimating(false);

      // Push a new history state when advancing to a new step
      window.history.pushState(null, document.title, window.location.pathname);
    }, 300);
  };

  const prevStep = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep((prev) => prev - 1);
      setIsAnimating(false);
    }, 300);
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation for step 3
    if (!formData.persons) {
      newErrors.persons = "Please enter number of passengers";
    }
    if (!formData.luggage) {
      newErrors.luggage = "Please enter number of luggage items";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Combine date and time
    const pickupDateTime = new Date(formData.pickUpDate);
    pickupDateTime.setHours(
      parseInt(formData.pickupHours),
      parseInt(formData.pickupMinutes)
    );

    const dropoffDateTime = new Date(formData.dropOffDate);
    dropoffDateTime.setHours(
      parseInt(formData.dropoffHours),
      parseInt(formData.dropoffMinutes)
    );

    try {
      // Prepare the data
      const transferData = {
        ...formData,
        pickUpDate: pickupDateTime,
        dropOffDate: dropoffDateTime,
        priceTotal: totalPriceEstimate,
        pickupLocation:
          formData.pickupLocation === "other"
            ? formData.otherPickupLocation
            : locationOptions.find(
                (opt) => opt.value === formData.pickupLocation
              )?.label,
        dropoffLocation:
          formData.dropoffLocation === "other"
            ? formData.otherDropoffLocation
            : locationOptions.find(
                (opt) => opt.value === formData.dropoffLocation
              )?.label,
      };

      // First submit the transfer request
      let transferResponse;
      try {
        transferResponse = await axios.post(
          "http://localhost:5000/api/contact/transfer",
          transferData
        );
      } catch (error) {
        console.error("Error submitting transfer request:", error);
        throw error;
      }

      // If tour is selected, book the tour and associate it with the transfer
      if (formData.tourId) {
        try {
          // First get the most recently created transfer for this user
          const userTransfersResponse = await axios.get(
            `http://localhost:5000/api/contact/transfers?email=${encodeURIComponent(
              formData.email
            )}&recent=true`
          );
          // Check if we got a transfer and extract its ID
          if (
            userTransfersResponse.data &&
            userTransfersResponse.data.length > 0
          ) {
            const transferId = userTransfersResponse.data[0]._id;
            const tourBookingData = {
              transfer: transferId,
              tour: formData.tourId,
              vehicleType: formData.carType || "car",
              tourDate: formData.tourDate,
              tourTime: formData.tourTime,
              numberOfPersons: formData.tourPersons,
        travelGuide: {
          available: !!formData.travelGuide.available,
          time: formData.travelGuide.available ? formData.travelGuide.time : "",
        },
              notes: formData.additionalInfo,
              email: formData.email,
              name: formData.name + " " + formData.surname,
              phone: formData.phone,
              pickupLocation: formData.pickupLocation,
              dropoffLocation: formData.dropoffLocation,
              paymentMethod: formData.paymentMethod,
              price: formData.tourPrice,
            };


            await axios.post(
              "http://localhost:5000/api/tour-bookings",
              tourBookingData
            );
          } else {
            throw new Error("Could not find the transfer ID for tour booking");
          }
        } catch (tourError) {
          console.error("Error booking tour:", tourError);
          Swal.fire({
            title: "Warning",
            text: "Transfer was booked, but we couldn't book your tour. Please contact support.",
            icon: "warning",
            confirmButtonColor: "#1e3a8a",
          });
        }
      }

      Swal.fire({
        title: "Success!",
        text: `Your transfer request has been submitted successfully!`,
        icon: "success",
        confirmButtonColor: "#1e3a8a",
      }).then(() => {
        // Reset form
        setFormData({
          pickUpDate: new Date(),
          dropOffDate: new Date(),
          pickupLocation: "",
          dropoffLocation: "",
          otherPickupLocation: "",
          otherDropoffLocation: "",
          pickupHours: "12",
          pickupMinutes: "00",
          dropoffHours: "12",
          dropoffMinutes: "00",
          name: "",
          surname: "",
          email: "",
          phone: "",
          persons: "",
          luggage: "",
          serviceType: "one-way",
          carType: "",
          additionalInfo: "",
          paymentMethod: "credit-card",
          isReturn: false,
          airportTicketId: "",
          helicopterTransfer: false,
          atDisposal: false,
          disposalHours: "3",
        });
        setActiveStep(1);
        setVehicleType("");
      });
    } catch (error) {
      console.error("Error submitting transfer request:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to submit your request. Please try again.",
        icon: "error",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const backgroundImage = require("../../assets/images/athens.jpg");

  return (
    <>
    <main
      className="flex-grow bg-gradient-to-br from-blue-900 via-indigo-900 to-gray-900 bg-cover bg-center bg-no-repeat py-16 relative overflow-hidden"
      id="transfer-form"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {/* Animated Background Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-md z-0"></div>

      {/* Booking Choice Buttons */}
      {!formData.bookingType && activeStep <= 1 && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] relative z-10 p-4">
          <div className="bg-gradient-to-br from-white/30 via-indigo-200/30 to-blue-100/20 p-12 md:p-20 shadow-2xl rounded-3xl border border-white/30 max-w-3xl mx-auto text-center animate-fadeInUp">
            <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-blue-400 to-purple-500 mb-6 drop-shadow-lg animate-fadeIn">
              Welcome to Athens Transfers & Tours
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-10 animate-fadeIn delay-200">
              Discover the beauty of Athens with our premium transfer services and unforgettable tours. Whether you need a seamless ride across the city or wish to explore ancient wonders, we have you covered. <span className="font-semibold text-indigo-200">What would you like to book today?</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-8 justify-center items-center animate-fadeIn delay-300">
              <button
                className="group flex items-center gap-4 px-10 py-5 rounded-2xl bg-gradient-to-r from-gray-800 via-gray-900 to-black text-white font-bold text-xl shadow-xl hover:from-indigo-800 hover:to-blue-900 transition-all duration-300 border-2 border-white/20 hover:border-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-400/60 transform hover:scale-105 active:scale-95 animate-bounceIn"
                aria-label="Book an Athens Transfer"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    bookingType: "transfer",
                  }));
                  setActiveStep(1);
                }}
              >
                <FaCarSide className="text-3xl text-indigo-300 group-hover:animate-wiggle" />
                Book an Athens Transfer
              </button>
              <button
                className="group flex items-center gap-4 px-10 py-5 rounded-2xl bg-gradient-to-r from-indigo-600 via-blue-700 to-purple-700 text-white font-bold text-xl shadow-xl hover:from-purple-800 hover:to-indigo-900 transition-all duration-300 border-2 border-white/20 hover:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-400/60 transform hover:scale-105 active:scale-95 animate-bounceIn delay-150"
                aria-label="Book a Tour"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, bookingType: "tour" }));
                  setActiveStep(1);
                }}
              >
                <FaLandmark className="text-3xl text-yellow-200 group-hover:animate-wiggle" />
                Book a Tour
              </button>
            </div>
            <div className="mt-8 text-sm text-gray-300 animate-fadeIn delay-400">
              <span className="font-semibold text-indigo-200">Why choose us?</span> Enjoy reliable, comfortable, and stylish journeys with our expert drivers and guides. Your Athens adventure starts here!
            </div>
          </div>
        </div>
      )}

      {/* Go Back / Change Booking Type Button */}
      {formData.bookingType && activeStep >= 1 && (
        <div className="container mx-auto px-4 mb-4 flex justify-start relative z-20">
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/90 hover:bg-white text-gray-800 font-medium shadow border border-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-400"
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                bookingType: "",
              }));
              setActiveStep(1);
            }}
            type="button"
            aria-label="Change Booking Type"
          >
            <IoClose className="text-lg" />
            Change Booking Type
          </button>
        </div>
      )}

        {/* Show TourForm if bookingType is 'tour' */}
        {formData.bookingType === "tour" && activeStep === 1 && (
          <div className="flex items-center justify-center">
            <TourForm />
          </div>
        )}

        {/* Show the rest of the form if bookingType is 'transfer' */}
        {formData.bookingType === "transfer" && (
          <>
            {/* Loading overlay for GPS permission */}
            {loadingLocation && (
              <div className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm flex items-center justify-center">
                <div className="bg-white/10 backdrop-blur-md p-6 rounded-lg text-white text-center max-w-xs w-full mx-4">
                  <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="font-medium">Detecting your location...</p>
                  <p className="text-sm mt-2">
                    This helps us offer better pickup options
                  </p>
                  <button
                    onClick={() => setLoadingLocation(false)}
                    className="mt-4 px-4 py-2 bg-gray-800 hover:bg-black text-white rounded-md transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}
            <div className="container mx-auto py-8 px-4 relative z-10">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl shadow-2xl overflow-hidden border border-white/20 relative">
                {/* Progress bar */}
                <div className="h-1.5 bg-gray-200/30">
                  <div
                    className="h-full bg-gradient-to-r from-gray-700 to-black transition-all duration-300 ease-in-out"
                    style={{ width: `${(activeStep / 4) * 100}%` }}
                  ></div>
                </div>
                {/* Dark overlay for better contrast */}
                <div className="absolute inset-0 bg-black/30 backdrop-blur-sm -z-10 rounded-xl"></div>
                {/* Form Content */}
                <div className="p-6 md:p-10 flex flex-row">
                  <form
                    onSubmit={handleSubmit}
                    className="relative w-full max-w-[100%] lg:max-w-[66%]"
                  >
                    <div
                      className={`transition-opacity duration-300 ${
                        isAnimating ? "opacity-0" : "opacity-100"
                      }`}
                    >
                      {/* Step 1: Vehicle Selection & Travel Details */}
                      {activeStep === 1 && (
                        <div className="space-y-10 animate-fadeIn mr-0 lg:mr-10">
                          <h2 className="text-2xl md:text-3xl font-bold text-gray-200 mb-6">
                            Book Your Transfer
                          </h2>

                          {/* Vehicle Selection */}
                          <div className="relative">
                            <h3 className="text-xl font-semibold text-gray-200 mb-4">
                              Choose Vehicle Type
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 max-w-4xl">
                              {carTypeOptions.map((option) => (
                                <motion.div
                                  key={option.value}
                                  onClick={() =>
                                    handleVehicleSelect(option.value)
                                  }
                                  className={`relative cursor-pointer rounded-xl shadow-md overflow-hidden transition-all duration-300 border-2 ${
                                    vehicleType === option.value
                                      ? "border-black ring-2 ring-gray-400"
                                      : "border-white/30 hover:border-gray-400"
                                  }`}
                                  whileHover={{
                                    y: -5,
                                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                                  }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <div className="p-5 text-center h-full bg-gradient-to-b from-white/30 to-gray-200/90 backdrop-blur-sm">
                                    <div className="w-full h-28 flex items-center justify-center mb-3 overflow-hidden">
                                      <img
                                        src={(() => {
                                          switch (option.value) {
                                            case "car":
                                              return require("../../assets/images/emerald_green.webp");
                                            case "minivan":
                                              return require("../../assets/images/mercedes-vito_tourer_1.png");
                                            case "van":
                                              return require("../../assets/images/V-Class-Marco-Polo-1.png");
                                            case "minibus":
                                              return require("../../assets/images/fleet-Mercedes-Benz-Sprinter-Passenger-Van.png");
                                            default:
                                              return require("../../assets/images/mercedes-vito_tourer_1.png");
                                          }
                                        })()}
                                        alt={option.label}
                                        className="object-contain max-h-full max-w-full"
                                      />
                                    </div>
                                    <h4 className="font-semibold text-lg text-gray-800">
                                      {option.label}
                                    </h4>
                                    <p className="text-xs text-gray-600 mt-2">
                                      {option.description}
                                    </p>
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                            {errors.vehicleType && (
                              <p className="text-red-800 text-sm mt-1">
                                {errors.vehicleType}
                              </p>
                            )}
                          </div>
                          {/* Location and Date */}
                          <div className="max-w-4xl">
                            <h3 className="text-xl font-semibold text-gray-200 mb-4">
                              Travel Details
                            </h3>

                            <div className="space-y-6">
                              {/* Pickup Location */}{" "}
                              <div className="space-y-6">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                  <div className="lg:col-span-2">
                                    <label className="font-medium text-gray-100 flex items-center mb-2">
                                      <FiMapPin className="mr-2 text-indigo-200" />
                                      Pickup Location
                                      {formData.pickupLocation === "other" &&
                                        formData.otherPickupLocation && (
                                          <span className="ml-2 text-xs bg-blue-500/70 text-white px-2 py-0.5 rounded-full">
                                            Using your location
                                          </span>
                                        )}
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                      <select
                                        name="pickupLocation"
                                        value={formData.pickupLocation}
                                        onChange={handleChange}
                                        className={`flex-1 border ${
                                          errors.pickupLocation
                                            ? "border-red-500"
                                            : "border-white/30"
                                        } rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white/70 backdrop-blur-sm`}
                                      >
                                        {" "}
                                        <option value="">
                                          Select pickup location
                                        </option>
                                        {locationOptions.map((option) => (
                                          <option
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.icon} {option.label}
                                          </option>
                                        ))}
                                      </select>

                                      <div className="flex items-center gap-2">
                                        <DatePicker
                                          selected={formData.pickUpDate}
                                          onChange={(date) =>
                                            handleDateChange(date, "pickUpDate")
                                          }
                                          className="w-32 border border-white/30 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm"
                                          minDate={new Date()}
                                          dateFormat="dd/MM/yyyy"
                                        />{" "}
                                        <div className="flex items-center bg-white/70 backdrop-blur-sm border border-white/30 rounded-lg">
                                          <select
                                            name="pickupHours"
                                            value={formData.pickupHours}
                                            onChange={handleChange}
                                            className="p-3 pr-1 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-transparent"
                                          >
                                            {[...Array(24)].map((_, i) => (
                                              <option
                                                key={i}
                                                value={i
                                                  .toString()
                                                  .padStart(2, "0")}
                                              >
                                                {i.toString().padStart(2, "0")}
                                              </option>
                                            ))}
                                          </select>
                                          <span className="text-gray-600">
                                            :
                                          </span>
                                          <select
                                            name="pickupMinutes"
                                            value={formData.pickupMinutes}
                                            onChange={handleChange}
                                            className="p-3 pl-1 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-transparent"
                                          >
                                            {["00", "15", "30", "45"].map(
                                              (min) => (
                                                <option key={min} value={min}>
                                                  {min}
                                                </option>
                                              )
                                            )}
                                          </select>
                                        </div>
                                      </div>
                                    </div>

                                    {formData.pickupLocation === "other" && (
                                      <input
                                        type="text"
                                        name="otherPickupLocation"
                                        value={formData.otherPickupLocation}
                                        onChange={handleChange}
                                        placeholder="Enter location details"
                                        className={`w-full mt-2 border ${
                                          errors.otherPickupLocation
                                            ? "border-red-500"
                                            : "border-gray-300"
                                        } rounded-lg p-3 text-gray-800 placeholder-gray-300 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm placeholder-shown:bg-white/30`}
                                      />
                                    )}

                                    {errors.pickupLocation && (
                                      <p className="text-red-800 text-sm mt-1">
                                        {errors.pickupLocation}
                                      </p>
                                    )}
                                    {errors.otherPickupLocation && (
                                      <p className="text-red-800 text-sm mt-1">
                                        {errors.otherPickupLocation}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                  <div className="lg:col-span-2">
                                    <label className="font-medium text-gray-100 flex items-center mb-2">
                                      <FiMapPin className="mr-2 text-indigo-200" />
                                      Drop-off Location
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                      <select
                                        name="dropoffLocation"
                                        value={formData.dropoffLocation}
                                        onChange={handleChange}
                                        className={`flex-1 border ${
                                          errors.dropoffLocation
                                            ? "border-red-500"
                                            : "border-gray-300"
                                        } rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none appearance-none bg-white/70 backdrop-blur-sm`}
                                      >
                                        {" "}
                                        <option value="">
                                          Select drop-off location
                                        </option>
                                        {locationOptions.map((option) => (
                                          <option
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.icon} {option.label}
                                          </option>
                                        ))}
                                      </select>

                                      <div className="flex items-center gap-2">
                                        <DatePicker
                                          selected={formData.dropOffDate}
                                          onChange={(date) =>
                                            handleDateChange(
                                              date,
                                              "dropOffDate"
                                            )
                                          }
                                          className="w-32 border border-white/30 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm"
                                          minDate={formData.pickUpDate}
                                          dateFormat="dd/MM/yyyy"
                                        />
                                        <div className="flex items-center bg-white/70 backdrop-blur-sm border border-white/30 rounded-lg">
                                          <select
                                            name="dropoffHours"
                                            value={formData.dropoffHours}
                                            onChange={handleChange}
                                            className="p-3 pr-1 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-transparent"
                                          >
                                            {[...Array(24)].map((_, i) => (
                                              <option
                                                key={i}
                                                value={i
                                                  .toString()
                                                  .padStart(2, "0")}
                                              >
                                                {i.toString().padStart(2, "0")}
                                              </option>
                                            ))}
                                          </select>
                                          <span className="text-gray-600">
                                            :
                                          </span>
                                          <select
                                            name="dropoffMinutes"
                                            value={formData.dropoffMinutes}
                                            onChange={handleChange}
                                            className="p-3 pl-1 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none appearance-none bg-transparent"
                                          >
                                            {["00", "15", "30", "45"].map(
                                              (min) => (
                                                <option key={min} value={min}>
                                                  {min}
                                                </option>
                                              )
                                            )}
                                          </select>
                                        </div>
                                      </div>
                                    </div>

                                    {formData.dropoffLocation === "other" && (
                                      <input
                                        type="text"
                                        name="otherDropoffLocation"
                                        value={formData.otherDropoffLocation}
                                        onChange={handleChange}
                                        placeholder="Enter location details"
                                        className={`w-full mt-2 border ${
                                          errors.otherDropoffLocation
                                            ? "border-red-500"
                                            : "border-gray-300"
                                        } rounded-lg p-3 text-gray-800 placeholder-gray-300 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm placeholder-shown:bg-white/30`}
                                      />
                                    )}

                                    {errors.dropoffLocation && (
                                      <p className="text-red-800 text-sm mt-1">
                                        {errors.dropoffLocation}
                                      </p>
                                    )}
                                    {errors.otherDropoffLocation && (
                                      <p className="text-red-800 text-sm mt-1">
                                        {errors.otherDropoffLocation}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {/* Return toggle */}
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="returnToggle"
                                  name="isReturn"
                                  checked={formData.isReturn}
                                  onChange={handleChange}
                                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <label
                                  htmlFor="returnToggle"
                                  className="ml-2 text-sm font-medium text-gray-200"
                                >
                                  Return trip
                                </label>
                              </div>
                              {/* Return Details (conditional) */}
                              {formData.isReturn && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2 bg-blue-50/40 backdrop-blur-sm p-4 rounded-lg border border-blue-100/50">
                                  <div className="space-y-2">
                                    <label className="font-medium text-gray-200 flex items-center">
                                      <FiMapPin className="mr-2 text-indigo-600" />
                                      Return Location
                                    </label>
                                    <select
                                      name="returnLocation"
                                      value={formData.returnLocation}
                                      onChange={handleChange}
                                      className={`w-full border ${
                                        errors.returnLocation
                                          ? "border-red-500"
                                          : "border-gray-300"
                                      } rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none appearance-none`}
                                    >
                                      <option value="">
                                        Select return location
                                      </option>
                                      {locationOptions.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>

                                    {errors.returnLocation && (
                                      <p className="text-red-500 text-sm">
                                        {errors.returnLocation}
                                      </p>
                                    )}

                                    {formData.returnLocation === "other" && (
                                      <input
                                        type="text"
                                        name="otherReturnLocation"
                                        value={formData.otherReturnLocation}
                                        onChange={handleChange}
                                        placeholder="Enter location details"
                                        className={`w-full mt-2 border ${
                                          errors.otherReturnLocation
                                            ? "border-red-500"
                                            : "border-gray-300"
                                        } rounded-lg p-3 text-gray-800 placeholder-gray-300 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm placeholder-shown:bg-white/30`}
                                      />
                                    )}

                                    {errors.otherReturnLocation && (
                                      <p className="text-red-500 text-sm">
                                        {errors.otherReturnLocation}
                                      </p>
                                    )}
                                  </div>

                                  <div className="space-y-2">
                                    <label className="font-medium text-gray-200 flex items-center">
                                      <FiCalendar className="mr-2 text-indigo-600" />
                                      Return Date & Time
                                    </label>
                                    <div className="flex space-x-2">
                                      <div className="relative flex-1">
                                        <DatePicker
                                          selected={formData.returnDate}
                                          onChange={(date) =>
                                            handleDateChange(date, "returnDate")
                                          }
                                          className="w-full border border-gray-300 rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                                          minDate={formData.pickUpDate}
                                          dateFormat="dd/MM/yyyy"
                                        />
                                      </div>
                                      <div className="flex space-x-1 items-center">
                                        <select
                                          name="returnHours"
                                          value={formData.returnHours}
                                          onChange={handleChange}
                                          className="border border-gray-300 rounded-lg p-3 pr-3 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none appearance-none"
                                        >
                                          {[...Array(24)].map((_, i) => (
                                            <option
                                              key={i}
                                              value={i
                                                .toString()
                                                .padStart(2, "0")}
                                            >
                                              {i.toString().padStart(2, "0")}
                                            </option>
                                          ))}
                                        </select>
                                        <span className="text-lg font-medium text-gray-600">
                                          :
                                        </span>
                                        <select
                                          name="returnMinutes"
                                          value={formData.returnMinutes}
                                          onChange={handleChange}
                                          className="border border-gray-300 rounded-lg p-3 pr-3 text-gray-800 focus:ring-2 focus:ring-indigo-500 outline-none appearance-none"
                                        >
                                          {["00", "15", "30", "45"].map(
                                            (min) => (
                                              <option key={min} value={min}>
                                                {min}
                                              </option>
                                            )
                                          )}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {/* Payment Method */}
                              <div className="mt-4">
                                <label className="font-medium text-gray-200 flex items-center mb-3">
                                  <FiCreditCard className="mr-2 text-indigo-200" />
                                  Payment Method
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {["Credit Card", "Cash"].map((method) => (
                                    <label
                                      key={method}
                                      className="relative cursor-pointer"
                                    >
                                      <input
                                        type="radio"
                                        name="paymentMethod"
                                        value={method
                                          .toLowerCase()
                                          .replace(" ", "-")}
                                        checked={
                                          formData.paymentMethod ===
                                          method.toLowerCase().replace(" ", "-")
                                        }
                                        onChange={handleChange}
                                        className="sr-only"
                                      />
                                      <div
                                        className={`flex items-center justify-center p-3 rounded-lg border-2 transition-all ${
                                          formData.paymentMethod ===
                                          method.toLowerCase().replace(" ", "-")
                                            ? "border-gray-800 bg-gray-200"
                                            : "border-gray-200 hover:border-gray-300"
                                        }`}
                                      >
                                        <FiCreditCard
                                          className={`mr-2 ${
                                            formData.paymentMethod ===
                                            method
                                              .toLowerCase()
                                              .replace(" ", "-")
                                              ? "text-gray-800"
                                              : "text-gray-400"
                                          }`}
                                        />{" "}
                                        <span
                                          className={
                                            formData.paymentMethod ===
                                            method
                                              .toLowerCase()
                                              .replace(" ", "-")
                                              ? "font-medium text-gray-800"
                                              : "text-gray-200"
                                          }
                                        >
                                          {method}
                                        </span>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              </div>
                              {/* Price Estimation Card - Step 1 */}
                              {(totalPriceEstimate !== null ||
                                loadingPrice ||
                                priceToBeDecided) && (
                                <div className="mt-6 p-4 border border-gray-300 rounded-lg bg-gradient-to-t from-gray-200/50 to-white/30 max-w-md">
                                  <h4 className="font-semibold text-gray-800 mb-2">
                                    Price Estimation
                                  </h4>
                                  {loadingPrice ? (
                                    <div className="flex items-center space-x-2">
                                      <div className="w-4 h-4 border-2 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
                                      <span className="text-gray-800">
                                        Calculating price...
                                      </span>
                                    </div>
                                  ) : priceToBeDecided ? (
                                    <div>
                                      <p className="text-gray-800 italic">
                                        Price to be determined
                                      </p>
                                      <p className="text-xs text-gray-600 mt-2">
                                        Custom routes require a quote. Please
                                        submit your request and we'll contact
                                        you with pricing.
                                      </p>
                                    </div>
                                  ) : totalPriceEstimate !== null ? (
                                    <div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-gray-200">
                                          {season === "low"
                                            ? "Low Season"
                                            : season === "high"
                                            ? "High Season"
                                            : "Medium Season"}{" "}
                                          Price:
                                        </span>
                                        <span className="text-2xl font-bold text-gray-900">
                                          {totalPriceEstimate} €
                                        </span>
                                      </div>
                                      {formData.isReturn && (
                                        <div className="mt-2 text-sm border-t border-gray-300/30 pt-2">
                                          <div className="flex justify-between items-center">
                                            <span className="text-gray-600">
                                              One way
                                            </span>
                                            <span className="font-medium text-gray-800">
                                              {priceEstimate &&
                                              priceEstimate > 0
                                                ? `${priceEstimate} €`
                                                : "To be determined"}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center">
                                            <span className="text-gray-600">
                                              Return trip included
                                            </span>
                                            <span className="font-medium text-gray-800">
                                              {totalPriceEstimate -
                                                priceEstimate ===
                                              0
                                                ? "To be determined"
                                                : `${
                                                    totalPriceEstimate -
                                                    priceEstimate
                                                  } €`}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      <p className="text-xs text-gray-300 mt-2">
                                        *Prices are subject to change based on
                                        availability and other factors
                                      </p>
                                    </div>
                                  ) : (
                                    <span className="text-gray-600">
                                      Select pickup and drop-off locations to
                                      see price
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Step 2: Personal Information */}
                      {activeStep === 2 && (
                        <div className="space-y-8 animate-fadeIn">
                          <h2 className="text-2xl md:text-3xl font-bold text-gray-200 mb-6">
                            Personal Information
                          </h2>
                          <div className="max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label className="font-medium text-gray-200">
                                Full Name
                              </label>
                              <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1">
                                  {" "}
                                  <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="First name"
                                    className={`w-full border ${
                                      errors.name
                                        ? "border-red-500"
                                        : "border-white/30"
                                    } rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm`}
                                  />
                                  {errors.name && (
                                    <p className="text-red-800 text-sm mt-1">
                                      {errors.name}
                                    </p>
                                  )}
                                </div>
                                <div className="flex-1">
                                  {" "}
                                  <input
                                    type="text"
                                    name="surname"
                                    value={formData.surname}
                                    onChange={handleChange}
                                    placeholder="Last name"
                                    className={`w-full border ${
                                      errors.surname
                                        ? "border-red-500"
                                        : "border-white/30"
                                    } rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm`}
                                  />
                                  {errors.surname && (
                                    <p className="text-red-800 text-sm mt-1">
                                      {errors.surname}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <label className="font-medium text-gray-200">
                                Contact Details
                              </label>{" "}
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Email address"
                                className={`w-full border ${
                                  errors.email
                                    ? "border-red-500"
                                    : "border-white/30"
                                } rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm`}
                              />
                              {errors.email && (
                                <p className="text-red-800 text-sm mt-1">
                                  {errors.email}
                                </p>
                              )}
                            </div>

                            <div className="space-y-2">
                              <label className="font-medium text-gray-200">
                                Phone Number
                              </label>
                              <div className="relative">
                                {" "}
                                <PhoneInput
                                  international
                                  defaultCountry="GR"
                                  value={formData.phone}
                                  onChange={(value) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      phone: value,
                                    }));
                                    handleChange({
                                      target: {
                                        name: "phone",
                                        value: value,
                                      },
                                    });
                                  }}
                                  placeholder="Phone number with country code"
                                  autoComplete="on"
                                />
                                {errors.phone && (
                                  <p className="text-red-800 text-sm mt-1">
                                    {errors.phone}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-2">
                                <label className="font-medium text-gray-200 flex items-center">
                                  <FiUser className="mr-2 text-blue-600" />
                                  People
                                </label>
                                <div className="relative">
                                  {" "}
                                  <input
                                    type="number"
                                    name="persons"
                                    value={formData.persons}
                                    onChange={handleChange}
                                    min="1"
                                    placeholder="Passengers"
                                    className={`w-full border ${
                                      errors.persons
                                        ? "border-red-500"
                                        : "border-white/30"
                                    } rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm`}
                                  />
                                  {errors.persons && (
                                    <p className="text-red-800 text-sm mt-1">
                                      {errors.persons}
                                    </p>
                                  )}
                                  {errors.personsRecommendation && (
                                    <p className="text-blue-500 text-sm mt-1">
                                      {errors.personsRecommendation}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="space-y-2">
                                <label className="font-medium text-gray-200">
                                  Luggage
                                </label>
                                <div className="relative">
                                  {" "}
                                  <input
                                    type="number"
                                    name="luggage"
                                    value={formData.luggage}
                                    onChange={handleChange}
                                    min="0"
                                    placeholder="Bags"
                                    className={`w-full border ${
                                      errors.luggage
                                        ? "border-red-500"
                                        : "border-white/30"
                                    } rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm`}
                                  />
                                  {errors.luggage && (
                                    <p className="text-red-800 text-sm mt-1">
                                      {errors.luggage}
                                    </p>
                                  )}
                                  {errors.luggageRecommendation && (
                                    <p className="text-blue-500 text-sm mt-1">
                                      {errors.luggageRecommendation}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>{" "}
                          <div className="max-w-4xl p-5 bg-blue-50/30 backdrop-blur-sm rounded-lg border border-white/30">
                            <div className="flex items-center text-indigo-100 mb-3">
                              <IoCalendarOutline className="mr-2 text-xl" />
                              <h4 className="font-semibold">Booking Details</h4>
                            </div>
                            <div className="backdrop-blur-sm p-4 rounded-lg border border-white/40 shadow-sm">
                              <div className="flex items-center justify-between mb-4 pb-2 border-b border-blue-50">
                                <h4 className="font-semibold text-indigo-100">
                                  Trip Details
                                </h4>
                                <div className="text-sm font-medium text-indigo-50">
                                  {carTypeOptions.find(
                                    (opt) => opt.value === vehicleType
                                  )?.label || "Vehicle not selected"}
                                </div>
                              </div>

                              {/* Price Display Section */}
                              {priceEstimate !== null && !priceToBeDecided && (
                                <div className="mb-4 p-3 bg-blue-50/40 backdrop-blur-sm rounded-lg border border-blue-100/30">
                                  <div className="flex items-center justify-between">
                                    <div className="text-gray-800 font-medium">
                                      Price Estimate (
                                      {season === "low"
                                        ? "Low"
                                        : season === "high"
                                        ? "High"
                                        : "Medium"}{" "}
                                      Season):
                                    </div>
                                    <div className="text-xl font-bold text-indigo-500">
                                      {totalPriceEstimate} €
                                    </div>
                                  </div>

                                  {formData.isReturn && (
                                    <div className="mt-2 text-sm border-t border-gray-200 pt-2">
                                      <div className="flex justify-between text-gray-600">
                                        <span>One way</span>
                                        <span className="font-medium text-gray-800">
                                          {priceEstimate && priceEstimate > 0
                                            ? `${priceEstimate} €`
                                            : "To be determined"}
                                        </span>
                                      </div>
                                      <div className="flex justify-between text-gray-600">
                                        <span>Return trip</span>
                                        <span className="font-medium text-gray-800">
                                          {returnPriceEstimate === 0 ||
                                          returnPriceEstimate === null
                                            ? "To be determined"
                                            : `${returnPriceEstimate} €`}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              {priceToBeDecided && (
                                <div className="mb-4 p-3 bg-amber-50/40 backdrop-blur-sm rounded-lg border border-amber-100/30">
                                  <div className="text-amber-700 font-medium">
                                    Price will be provided upon request
                                  </div>
                                  <p className="text-xs text-amber-600 mt-1">
                                    Custom route pricing requires a quote
                                  </p>
                                </div>
                              )}
                              {loadingPrice && (
                                <div className="mb-4 p-3 bg-blue-50 rounded-lg text-center">
                                  <span className="text-blue-600">
                                    Calculating price...
                                  </span>
                                </div>
                              )}

                              <div className="flex flex-col space-y-4">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0 w-24 text-sm font-medium text-gray-800">
                                    From
                                  </div>
                                  <div className="flex-grow">
                                    <p className="font-medium text-gray-900">
                                      {formData.pickupLocation
                                        ? formData.pickupLocation === "other"
                                          ? formData.otherPickupLocation
                                          : locationOptions.find(
                                              (opt) =>
                                                opt.value ===
                                                formData.pickupLocation
                                            )?.label
                                        : "Not specified"}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {formData.pickUpDate
                                        ? formData.pickUpDate.toLocaleDateString() +
                                          " at " +
                                          formData.pickupHours +
                                          ":" +
                                          formData.pickupMinutes
                                        : "Time not specified"}
                                    </p>
                                  </div>
                                </div>

                                <div className="h-8 border-l-2 border-dashed border-blue-200 ml-12"></div>

                                <div className="flex items-start">
                                  <div className="flex-shrink-0 w-24 text-sm font-medium text-gray-800">
                                    To
                                  </div>
                                  <div className="flex-grow">
                                    <p className="font-medium text-gray-900">
                                      {formData.dropoffLocation
                                        ? formData.dropoffLocation === "other"
                                          ? formData.otherDropoffLocation
                                          : locationOptions.find(
                                              (opt) =>
                                                opt.value ===
                                                formData.dropoffLocation
                                            )?.label
                                        : "Not specified"}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {formData.dropOffDate
                                        ? formData.dropOffDate.toLocaleDateString() +
                                          " at " +
                                          formData.dropoffHours +
                                          ":" +
                                          formData.dropoffMinutes
                                        : "Time not specified"}
                                    </p>
                                  </div>
                                </div>
                                <div className="h-8 border-l-2 border-dashed border-blue-200 ml-12"></div>
                                <div className="flex items-start">
                                  <div className="flex-shrink-0 w-24 text-sm font-medium text-gray-800">
                                    Return
                                  </div>
                                  <div className="flex-grow">
                                    <p className="font-medium text-gray-900">
                                      {formData.returnLocation
                                        ? formData.returnLocation === "other"
                                          ? formData.otherReturnLocation
                                          : locationOptions.find(
                                              (opt) =>
                                                opt.value ===
                                                formData.returnLocation
                                            )?.label
                                        : "Not specified"}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {formData.returnDate
                                        ? formData.returnDate.toLocaleDateString() +
                                          " at " +
                                          formData.returnHours +
                                          ":" +
                                          formData.returnMinutes
                                        : "Time not specified"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Step 3: Additional Details */}
                      {activeStep === 3 && (
                        <div className="space-y-8 animate-fadeIn">
                          <h2 className="text-2xl md:text-3xl font-bold text-gray-200 mb-6">
                            Additional Information
                          </h2>

                          {/* Tour Selection Section - Only show if tours are available */}
                          <TourSelection
                            formData={formData}
                            onChange={handleChange}
                            onTourSelect={handleTourSelect}
                            selectedTourId={formData.tourId}
                            render={(hasTours) => {
                              if (!hasTours) return null;

                              return (
                                <div className="max-w-4xl bg-white/30 backdrop-blur-sm rounded-lg border border-white/30 p-5">
                                  <div className="flex items-center mb-4">
                                    <FiMap className="mr-2 text-xl text-indigo-200" />
                                    <h3 className="text-xl font-semibold text-gray-200">
                                      Enhance Your Trip with a Guided Tour
                                    </h3>
                                  </div>
                                  <p className="text-gray-200 mb-4">
                                    Make the most of your visit to Athens by
                                    adding one of our exclusive guided tours to
                                    your transfer.
                                  </p>
                                </div>
                              );
                            }}
                          />

                          <div className="max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <label className="font-medium text-gray-200">
                                Airport Ticket ID / Boat Name (Optional)
                              </label>
                              <input
                                type="text"
                                name="airportTicketId"
                                value={formData.airportTicketId}
                                onChange={handleChange}
                                placeholder="Enter your flight number or boat name and time"
                                className="w-full border border-white/30 rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm placeholder-shown:bg-white/30"
                              />
                            </div>

                            <div className="space-y-4">
                              <div className="flex items-center mb-4">
                                <input
                                  type="checkbox"
                                  id="helicopterTransfer"
                                  name="helicopterTransfer"
                                  checked={formData.helicopterTransfer}
                                  onChange={handleChange}
                                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <label
                                  htmlFor="helicopterTransfer"
                                  className="ml-2 text-sm font-medium text-gray-200"
                                >
                                  Helicopter Transfer (Premium Service)
                                </label>
                              </div>

                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id="atDisposal"
                                  name="atDisposal"
                                  checked={formData.atDisposal}
                                  onChange={handleChange}
                                  className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <label
                                  htmlFor="atDisposal"
                                  className="ml-2 text-sm font-medium text-gray-200"
                                >
                                  Keep driver at your disposal (Premium Service)
                                </label>
                              </div>

                              {formData.atDisposal && (
                                <div className="mt-3 ml-6">
                                  <label className="block text-sm font-medium text-gray-200 mb-1">
                                    Hours needed
                                  </label>
                                  <select
                                    name="disposalHours"
                                    value={formData.disposalHours}
                                    onChange={handleChange}
                                    className="w-full border border-white/30 rounded-lg p-2 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white/70 backdrop-blur-sm"
                                  >
                                    {disposalHoursOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>

                                  {formData.disposalHours === "custom" && (
                                    <input
                                      type="number"
                                      name="customDisposalHours"
                                      value={formData.customDisposalHours}
                                      onChange={handleChange}
                                      min="1"
                                      placeholder="Enter hours"
                                      className="w-full mt-2 border border-white/30 rounded-lg p-2 text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="max-w-4xl space-y-4">
                            <label className="font-medium text-gray-200">
                              Additional Information
                            </label>
                            <textarea
                              name="additionalInfo"
                              value={formData.additionalInfo}
                              onChange={handleChange}
                              placeholder="Any special requests, infant seats, specific pickup instructions, etc."
                              className="w-full border border-white/30 rounded-lg p-3 h-32 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 backdrop-blur-sm placeholder-shown:bg-white/30"
                            ></textarea>
                          </div>

                          <div className="max-w-4xl p-5 bg-blue-50/30 backdrop-blur-sm rounded-lg">
                            <div className="flex items-center text-indigo-100 mb-3">
                              <IoCalendarOutline className="mr-2 text-xl" />
                              <h4 className="font-semibold">Booking Details</h4>
                            </div>
                            <div className="backdrop-blur-sm p-4 rounded-lg border border-white/40 shadow-sm">
                              <div className="flex items-center justify-between mb-4 pb-2 border-b border-blue-50">
                                <h4 className="font-semibold text-indigo-100">
                                  Trip Details
                                </h4>
                                <div className="text-sm font-medium text-indigo-50">
                                  {carTypeOptions.find(
                                    (opt) => opt.value === vehicleType
                                  )?.label || "Vehicle not selected"}
                                </div>
                              </div>

                              {/* Price Display Section */}
                              {priceEstimate !== null && !priceToBeDecided && (
                                <div className="mb-4 p-3 bg-blue-50/40 backdrop-blur-sm rounded-lg border border-blue-100/30">
                                  <div className="flex items-center justify-between">
                                    <div className="text-gray-800 font-medium">
                                      Price Estimate (
                                      {season === "low"
                                        ? "Low"
                                        : season === "high"
                                        ? "High"
                                        : "Medium"}{" "}
                                      Season):
                                    </div>
                                    <div className="text-xl font-bold text-indigo-500">
                                      {totalPriceEstimate} €
                                    </div>
                                  </div>

                                  {formData.isReturn && (
                                    <div className="mt-2 text-sm border-t border-gray-200 pt-2">
                                      <div className="flex justify-between text-gray-600">
                                        <span>One way</span>
                                        <span className="font-medium text-gray-800">
                                          {priceEstimate && priceEstimate > 0
                                            ? `${priceEstimate} €`
                                            : "To be determined"}
                                        </span>
                                      </div>
                                      <div className="flex justify-between text-gray-600">
                                        <span>Return trip</span>
                                        <span className="font-medium text-gray-800">
                                          {returnPriceEstimate === 0 ||
                                          returnPriceEstimate === null
                                            ? "To be determined"
                                            : `${returnPriceEstimate} €`}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  {/* if premium options are selected */}
                                  {formData.helicopterTransfer ||
                                  formData.atDisposal ? (
                                    <div className="mt-2 text-sm border-t border-gray-200 pt-2">
                                      <div className="flex justify-between text-gray-600">
                                        <span>Premium Options</span>
                                        <ul className="list-disc list-inside">
                                          {formData.helicopterTransfer && (
                                            <li className="font-medium text-gray-800">
                                              Helicopter Transfer*
                                            </li>
                                          )}
                                          {formData.atDisposal && (
                                            <li className="font-medium text-gray-800">
                                              Driver at Disposal*{" "}
                                              {" ( " +
                                                formData.disposalHours +
                                                " hours )"}
                                            </li>
                                          )}
                                        </ul>
                                      </div>
                                      {/* on the right */}
                                      <div className="flex justify-between text-gray-600">
                                        <span className="font-thing text-gray-500">
                                          *Price not Included
                                        </span>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              )}
                              {priceToBeDecided && (
                                <div className="mb-4 p-3 bg-amber-50/40 backdrop-blur-sm rounded-lg border border-amber-100/30">
                                  <div className="text-amber-700 font-medium">
                                    Price will be provided upon request
                                  </div>
                                  <p className="text-xs text-amber-600 mt-1">
                                    Custom route pricing requires a quote
                                  </p>
                                </div>
                              )}
                              {loadingPrice && (
                                <div className="mb-4 p-3 bg-blue-50 rounded-lg text-center">
                                  <span className="text-blue-600">
                                    Calculating price...
                                  </span>
                                </div>
                              )}

                              <div className="flex flex-col space-y-4">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0 w-24 text-sm font-medium text-gray-800">
                                    From
                                  </div>
                                  <div className="flex-grow">
                                    <p className="font-medium text-gray-900">
                                      {formData.pickupLocation
                                        ? formData.pickupLocation === "other"
                                          ? formData.otherPickupLocation
                                          : locationOptions.find(
                                              (opt) =>
                                                opt.value ===
                                                formData.pickupLocation
                                            )?.label
                                        : "Not specified"}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {formData.pickUpDate
                                        ? formData.pickUpDate.toLocaleDateString() +
                                          " at " +
                                          formData.pickupHours +
                                          ":" +
                                          formData.pickupMinutes
                                        : "Time not specified"}
                                    </p>
                                  </div>
                                </div>

                                <div className="h-8 border-l-2 border-dashed border-blue-200 ml-12"></div>

                                <div className="flex items-start">
                                  <div className="flex-shrink-0 w-24 text-sm font-medium text-gray-800">
                                    To
                                  </div>
                                  <div className="flex-grow">
                                    <p className="font-medium text-gray-900">
                                      {formData.dropoffLocation
                                        ? formData.dropoffLocation === "other"
                                          ? formData.otherDropoffLocation
                                          : locationOptions.find(
                                              (opt) =>
                                                opt.value ===
                                                formData.dropoffLocation
                                            )?.label
                                        : "Not specified"}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {formData.dropOffDate
                                        ? formData.dropOffDate.toLocaleDateString() +
                                          " at " +
                                          formData.dropoffHours +
                                          ":" +
                                          formData.dropoffMinutes
                                        : "Time not specified"}
                                    </p>
                                  </div>
                                </div>

                                {/* Add return trip details if applicable */}
                                {formData.isReturn && (
                                  <>
                                    <div className="h-8 border-l-2 border-dashed border-blue-200 ml-12"></div>
                                    <div className="flex items-start">
                                      <div className="flex-shrink-0 w-24 text-sm font-medium text-gray-500">
                                        Return
                                      </div>
                                      <div className="flex-grow">
                                        <p className="font-medium text-gray-900">
                                          Return to:{" "}
                                          {formData.returnLocation
                                            ? formData.returnLocation ===
                                              "other"
                                              ? formData.otherReturnLocation
                                              : locationOptions.find(
                                                  (opt) =>
                                                    opt.value ===
                                                    formData.returnLocation
                                                )?.label
                                            : "Not specified"}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                          {formData.dropOffDate
                                            ? formData.dropOffDate.toLocaleDateString() +
                                              " at " +
                                              formData.dropoffHours +
                                              ":" +
                                              formData.dropoffMinutes
                                            : "Time not specified"}
                                        </p>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Step 4: Final Confirmation */}
                      {activeStep === 4 && (
                        <div className="space-y-8 animate-fadeIn">
                          <h2 className="text-2xl md:text-3xl font-bold text-gray-200 mb-6">
                            Confirm Your Booking
                          </h2>

                          <div className="max-w-4xl space-y-6">
                            {/* Trip Summary Card */}
                            <div className="bg-white/30 backdrop-blur-md p-6 rounded-xl border border-white/20">
                              <h3 className="text-xl font-semibold text-gray-200 mb-4">
                                Trip Summary
                              </h3>
                              <div className="grid gap-6">
                                {/* Vehicle Details */}
                                <div className="bg-white/30 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-white/30">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h4 className="font-medium text-gray-800">
                                        {
                                          carTypeOptions.find(
                                            (opt) => opt.value === vehicleType
                                          )?.label
                                        }
                                      </h4>
                                      <p className="text-sm text-gray-500">
                                        {
                                          carTypeOptions.find(
                                            (opt) => opt.value === vehicleType
                                          )?.description
                                        }
                                      </p>
                                    </div>
                                    <div className="text-4xl text-blue-600">
                                      {carTypeOptions.find(
                                        (opt) => opt.value === vehicleType
                                      )?.icon &&
                                        React.createElement(
                                          carTypeOptions.find(
                                            (opt) => opt.value === vehicleType
                                          )?.icon
                                        )}
                                    </div>
                                  </div>
                                </div>

                                {/* Trip Details */}
                                <div className="bg-white/30 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-white/50">
                                  <h4 className="font-medium text-gray-80 mb-4">
                                    Journey Details
                                  </h4>
                                  <div className="space-y-4">
                                    {/* Pickup details */}
                                    <div className="flex items-start">
                                      <div className="flex-shrink-0 w-24">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                          <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                                        </div>
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-800">
                                          {formData.pickupLocation === "other"
                                            ? formData.otherPickupLocation
                                            : locationOptions.find(
                                                (opt) =>
                                                  opt.value ===
                                                  formData.pickupLocation
                                              )?.label}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                          {formData.pickUpDate?.toLocaleDateString()}{" "}
                                          at {formData.pickupHours}:
                                          {formData.pickupMinutes}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="flex">
                                      <div className="flex-shrink-0 w-24 flex justify-center">
                                        <div className="w-0.5 h-full bg-blue-200"></div>
                                      </div>
                                    </div>

                                    {/* Dropoff details */}
                                    <div className="flex items-start">
                                      <div className="flex-shrink-0 w-24">
                                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                          <div className="w-3 h-3 rounded-full bg-indigo-600"></div>
                                        </div>
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-800">
                                          {formData.dropoffLocation === "other"
                                            ? formData.otherDropoffLocation
                                            : locationOptions.find(
                                                (opt) =>
                                                  opt.value ===
                                                  formData.dropoffLocation
                                              )?.label}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                          {formData.dropOffDate?.toLocaleDateString()}{" "}
                                          at {formData.dropoffHours}:
                                          {formData.dropoffMinutes}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Return trip details if applicable */}
                                    {formData.isReturn && (
                                      <>
                                        <div className="flex">
                                          <div className="flex-shrink-0 w-24 flex justify-center">
                                            <div className="w-0.5 h-full bg-blue-200"></div>
                                          </div>
                                        </div>
                                        <div className="flex items-start">
                                          <div className="flex-shrink-0 w-24">
                                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                              <div className="w-3 h-3 rounded-full bg-green-600"></div>
                                            </div>
                                          </div>
                                          <div>
                                            <p className="font-medium text-gray-800">
                                              Return to:{" "}
                                              {formData.returnLocation ===
                                              "other"
                                                ? formData.otherReturnLocation
                                                : locationOptions.find(
                                                    (opt) =>
                                                      opt.value ===
                                                      formData.returnLocation
                                                  )?.label}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                              {formData.dropOffDate?.toLocaleDateString()}{" "}
                                              at {formData.dropoffHours}:
                                              {formData.dropoffMinutes}
                                            </p>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* Passenger Details */}
                            <div className="bg-white/30 backdrop-blur-md p-6 rounded-xl border border-white/20">
                              <h4 className="font-bold text-gray-200 mb-4">
                                Passenger Details
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm text-gray-300 font-bold">
                                    Full Name
                                  </p>
                                  <p className="font-medium text-gray-200">
                                    {formData.name} {formData.surname}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-300 font-bold">
                                    Contact
                                  </p>
                                  <p className="font-medium text-gray-200">
                                    {formData.phone}
                                  </p>
                                  <p className="text-sm text-gray-200">
                                    {formData.email}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-md text-gray-200 font-bold">
                                    Party Size
                                  </p>
                                  <p className="font-medium text-gray-200">
                                    {formData.persons} passengers
                                  </p>
                                  <p className="text-sm text-gray-200">
                                    {formData.luggage} luggage items
                                  </p>
                                </div>
                                <div>
                                  <p className="text-md text-gray-200 font-bold">
                                    Payment Method
                                  </p>
                                  <p className="font-medium text-gray-200">
                                    {formData.paymentMethod
                                      .replace("-", " ")
                                      .replace(/\b\w/g, (char) =>
                                        char.toUpperCase()
                                      )}
                                  </p>
                                </div>
                              </div>
                            </div>{" "}
                            {/* Additional Services */}
                            {(formData.helicopterTransfer ||
                              formData.atDisposal ||
                              formData.airportTicketId ||
                              formData.includeTour) && (
                              <div className="bg-white/70 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-white/30">
                                <h4 className="font-medium text-gray-800 mb-4">
                                  Additional Services & Information
                                </h4>
                                <div className="space-y-2">
                                  {formData.includeTour && formData.tourId && (
                                    
                                    <div className="mb-3 border-b border-gray-200 pb-3">
                                      <div className="flex items-center text-blue-600 font-medium mb-2">
                                        <svg
                                          className="w-5 h-5 mr-2"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                          />
                                        </svg>
                                        <span>Guided Tour</span>
                                      </div>
                                      <div className="pl-7 space-y-1">
                                        <p className="font-medium text-gray-800">
                                          {formData.tourName}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                          Date:{" "}
                                          {new Date(
                                            formData.tourDate
                                          ).toLocaleDateString()}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                          Participants: {formData.tourPersons}
                                        </p>
                                        <p className="text-sm font-medium text-blue-600">
                                          Tour Price: €{formData.tourPrice}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {formData.helicopterTransfer && (
                                    <div className="flex items-center text-blue-600">
                                      <svg
                                        className="w-5 h-5 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      <span>Helicopter Transfer Service*</span>
                                    </div>
                                  )}
                                  {formData.atDisposal && (
                                    <div className="flex items-center text-blue-600">
                                      <svg
                                        className="w-5 h-5 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      <span>
                                        Keep driver at your disposal* (
                                        {formData.disposalHours} hours)
                                      </span>
                                    </div>
                                  )}
                                  {formData.airportTicketId && (
                                    <div className="text-gray-800">
                                      <span className="font-medium">
                                        Flight/Ferry Number:
                                      </span>{" "}
                                      {formData.airportTicketId}
                                    </div>
                                  )}
                                  {formData.additionalInfo && (
                                    <div className="mt-2 text-gray-800">
                                      <p className="font-medium mb-1">
                                        Additional Notes:
                                      </p>
                                      <p className="text-sm text-gray-600">
                                        {formData.additionalInfo}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="max-w-4xl mt-8 bg-white/30 backdrop-blur-md p-6 rounded-lg border border-white/20">
                            <h4 className="font-medium text-gray-200 mb-4 flex items-center">
                              <FiMapPin className="mr-2 text-indigo-200" />
                              Service Highlights
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {[
                                "All drivers speak fluent Greek and English",
                                "Punctual and reliable private service",
                                "All vehicles are air-conditioned",
                                "Free cancellation up to 24 hours before pickup",
                                "Child seats available upon request",
                                "Local knowledge and recommendations",
                                "Fixed rates with no hidden fees",
                                "24/7 customer support",
                              ].map((info, i) => (
                                <div key={i} className="flex items-start">
                                  <div className="h-5 w-5 rounded-full bg-indigo-100 flex items-center justify-center mr-2 mt-0.5">
                                    <div className="h-2 w-2 rounded-full bg-indigo-500"></div>
                                  </div>
                                  <p className="text-sm text-gray-200">
                                    {info}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* price total */}
                          <div className="max-w-4xl bg-white/70 backdrop-blur-sm p-4 rounded-lg shadow-sm border border-white/30">
                            <h4 className="font-medium text-gray-800 mb-4">
                              Total Price Estimate
                            </h4>
                            {formData.isReturn && (
                              <div className="mt-2 text-sm border-t border-gray-200 pt-2">
                                <div className="flex justify-between text-gray-600">
                                  <span>One way</span>
                                  <span className="font-medium text-gray-800">
                                    {priceEstimate && priceEstimate > 0
                                      ? `${priceEstimate} €`
                                      : "To be determined"}
                                  </span>
                                </div>
                                <div className="flex justify-between border-b border-gray-200 mb-2 pb-2 text-gray-600">
                                  <span>Return trip</span>
                                  <span className="font-medium text-gray-800">
                                    {returnPriceEstimate === 0 ||
                                    returnPriceEstimate === null
                                      ? "To be determined"
                                      : `${returnPriceEstimate} €`}
                                  </span>
                                </div>
                              </div>
                            )}
                            {formData.includeTour && formData.tourPrice > 0 && (
                              <div className="mb-2 text-sm border-b border-gray-200 pb-2">
                                <div className="flex justify-between text-gray-600">
                                  <span>Transfer Price</span>
                                  <span className="font-medium text-gray-800">
                                    {totalPriceEstimate &&
                                    totalPriceEstimate > 0
                                      ? `${totalPriceEstimate} €`
                                      : "To be determined"}
                                  </span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                  <span>Tour: {formData.tourName}</span>
                                  <span className="font-medium text-gray-800">
                                    {formData.tourPrice} €
                                  </span>
                                </div>
                              </div>
                            )}
                            {formData.helicopterTransfer && (
                              <div className="mt-2 text-sm border-t border-gray-200 pt-2">
                                <div className="flex justify-between text-gray-600">
                                  <span>Helicopter Transfer</span>
                                  <span className="font-medium text-gray-800">
                                    Price not included
                                  </span>
                                </div>
                              </div>
                            )}
                            {formData.atDisposal && (
                              <div className="mb-2 text-sm pb-2 border-b border-gray-200">
                                <div className="flex justify-between text-gray-600">
                                  <span>Driver at Disposal</span>
                                  <span className="font-medium text-gray-800">
                                    Price not included
                                  </span>
                                </div>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <div className="text-gray-600">
                                Total Price Estimate:
                              </div>
                              <div className="text-xl font-bold text-indigo-500">
                                {formData.includeTour && formData.tourPrice
                                  ? totalPriceEstimate + formData.tourPrice
                                  : totalPriceEstimate}{" "}
                                €
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="acceptedTerms"
                              name="acceptedTerms"
                              checked={formData.acceptedTerms}
                              onChange={handleChange}
                              className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <label
                              htmlFor="acceptedTerms"
                              className="text-sm font-medium text-gray-200"
                            >
                              I accept that all the information provided is
                              correct.
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </form>
                  <div
                    className={`relative max-w-[34%] m-auto lg:flex hidden justify-center items-center p-6 ${
                      activeStep !== 4 ? "border-l-2 border-l-gray-200" : ""
                    } h-[50vh] overflow-hidden`}
                  >
                    {vehicleType && (
                      <AnimatedCar
                        vehicleType={vehicleType}
                        activeStep={activeStep}
                      />
                    )}
                  </div>
                </div>

                {/* Navigation Footer */}
                <div className="p-4 md:p-6 border-t border-gray-100/30 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/40 backdrop-blur-sm">
                  <button
                    onClick={() => {
                      if (activeStep > 1) {
                        prevStep();
                        document
                          .getElementById("transfer-form")
                          .scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className={`px-4 md:px-6 py-2 md:py-2.5 rounded-lg font-medium transition-all duration-300 w-full sm:w-auto ${
                      activeStep > 1
                        ? "text-gray-200 hover:bg-gray-800 cursor-pointer"
                        : "text-gray-400 cursor-default"
                    }`}
                    disabled={activeStep === 1}
                  >
                    {activeStep > 1 ? "Previous" : "Start"}
                  </button>

                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 w-full sm:w-auto">
                    <div className="hidden sm:flex space-x-3">
                      {[1, 2, 3, 4].map((step) => (
                        <div
                          key={step}
                          className={`w-3 h-3 rounded-full transition-all duration-300 ${
                            activeStep === step
                              ? "bg-black scale-125"
                              : activeStep > step
                              ? "bg-gray-600"
                              : "bg-gray-300"
                          }`}
                        ></div>
                      ))}
                    </div>

                    {activeStep < 4 ? (
                      <button
                        type="button"
                        onClick={() => {
                          nextStep();
                          document
                            .getElementById("transfer-form")
                            .scrollIntoView({ behavior: "smooth" });
                        }}
                        className={`w-full sm:w-auto relative overflow-hidden px-6 md:px-8 py-2 md:py-2.5 rounded-lg font-medium transition-all duration-300 ${
                          activeStep === 3
                            ? "bg-gradient-to-r from-gray-700 to-black hover:from-black hover:to-gray-800 text-white"
                            : "bg-gradient-to-r from-gray-700 to-black hover:from-black hover:to-gray-800 text-white"
                        }`}
                      >
                        <span className="relative z-10">
                          {activeStep === 3 ? "Review Booking" : "Continue"}
                        </span>
                        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity"></div>
                      </button>
                    ) : (
                      <button
                        type="submit"
                        onClick={(e) => {
                          handleSubmit(e);
                          document
                            .getElementById("transfer-form")
                            .scrollIntoView({ behavior: "smooth" });
                        }}
                        disabled={isSubmitting || !formData.acceptedTerms}
                        className="w-full sm:w-auto relative overflow-hidden px-6 md:px-8 py-2 md:py-2.5 rounded-lg font-medium transition-all duration-300 bg-gradient-to-r from-gray-800 to-black hover:from-black hover:to-gray-800 text-white disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          <>
                            <span className="relative z-10">
                              Confirm Booking
                            </span>
                            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity"></div>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .animate-pulse {
          animation: pulse 1.5s infinite;
        }

        .animate-bounce {
          animation: bounce 0.8s infinite;
        }
      `}</style>
    </>
  );
};

export default TransferForm;
